import Column from '@app/components/layout/Column'
import { Breadcrumbs } from '@app/components/core/breadcrumbs/Breadcrumbs'
import { InitiativeViewPageLayout } from '@app/pages/layout/InitiativeViewPageLayout'
import { useEffect, useState } from 'react'
import { useClient, useAuthState } from '@based/sdk'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { Title } from '@app/components/core/typography/Title'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { useParamState } from '@app/util/hooks/useParamState'
import { INITIATIVE_USER_ROLES, InitiativeUserRole } from '@schema/initiative'
import { match } from 'ts-pattern'
import { InitiativeUser } from '../sections/InitiativeUser'
import { Action } from 'functions/app/dashboard/initiative/manage-participant'
import {
  DashboardInitiativeModel,
  DashboardInitiativeUser,
} from 'functions/app/dashboard/initiative/list-users'
import { fullName } from '@app/util/helpers'
import { useToast } from '@app/util/hooks/useToast'
import { useMessageBox } from '@app/util/hooks/useMessageBox'
import {
  INITIATIVE_PEOPLE_TABS,
  INITIATIVE_PEOPLE_TAB_PARAM_KEY,
  type InitiativePeopleTab,
} from './constants'

export const InitiativePeople = ({
  initiativeId,
}: {
  initiativeId: string
}) => {
  const [view, setValue] = useParamState(
    INITIATIVE_PEOPLE_TABS,
    INITIATIVE_PEOPLE_TAB_PARAM_KEY,
  )
  const client = useClient()
  const authState = useAuthState()
  const translationsRoot = useTranslations().app
  const translations = translationsRoot.dashboard.initiative
  const [users, setUsers] = useState<DashboardInitiativeUser[]>([])
  const [initiativeTitle, setInitiativeTitle] = useState<string>(null)
  const { showMessageBox } = useMessageBox()
  const { showFailToast, showSuccessToast } = useToast()
  useEffect(() => {
    client
      .query('list-initiative-users', {
        initiativeId: Number(initiativeId),
        userId: authState.userId,
      })
      .subscribe((result: DashboardInitiativeModel) => {
        setInitiativeTitle(result.title ?? '')
        setUsers(result.users)
      })
  }, [client, initiativeId, authState, setUsers])

  const manage = async (userId: number, action: Action): Promise<void> => {
    await client
      .call('initiative-manage-participant', {
        initiativeId: Number(initiativeId),
        userId,
        action,
      })
      .then(() => {
        showSuccessToast(translations.people.participantStatusChanged)
      })
      .catch(() => {
        showFailToast()
      })
  }

  const handleAction = async (
    user: DashboardInitiativeUser,
    action: Action | 'message',
  ) => {
    if (action === 'message') {
      showMessageBox(
        users.map((u) => ({
          id: u.id,
          name: fullName(u),
          avatar: u.avatar,
          role: u.$role,
        })),
        Number(initiativeId),
        user.id,
      )
    } else {
      await manage(user.id, action)
    }
  }

  if (!initiativeTitle) {
    return (
      <InitiativeViewPageLayout>
        <Column alignItems="stretch" gap="1.5rem">
          <Breadcrumbs
            levels={[
              {
                label: translations.common.myInitiatives,
                to: pages.dashboardInitiatives,
              },
              {
                label: translations.common.loading,
              },
            ]}
          />
        </Column>
      </InitiativeViewPageLayout>
    )
  }

  const title =
    initiativeTitle.trim() !== ''
      ? initiativeTitle
      : translations.common.unnamedInitiative

  return (
    <InitiativeViewPageLayout>
      <Column alignItems="stretch" gap="1.5rem" style={{ flexGrow: 0 }}>
        <Breadcrumbs
          levels={[
            {
              label: translations.common.myInitiatives,
              to: pages.dashboardInitiatives,
            },
            {
              label: title,
              to: pages.dashboardViewInitiative(initiativeId),
            },
            {
              label: translations.people.people,
            },
          ]}
        />
        <Column gap="0.5rem" alignItems="stretch" style={{ flexGrow: 0 }}>
          <Title.H3>{translations.people.people}</Title.H3>
          <Column alignItems="stretch">
            <TabsGroup
              selectedTab={view}
              onSelectTab={setValue}
              tabs={[
                {
                  value: INITIATIVE_PEOPLE_TABS.members,
                  label: translations.people.members,
                },
                {
                  value: INITIATIVE_PEOPLE_TABS.followers,
                  label: translations.people.followers,
                },
                {
                  value: INITIATIVE_PEOPLE_TABS.admins,
                  label: translations.people.admins,
                },
                {
                  value: INITIATIVE_PEOPLE_TABS.requests,
                  label: translations.people.requests,
                },
              ]}
            />
            {users
              .filter((u) => mapRole(view).includes(u.$role))
              .map((u) => (
                <InitiativeUser
                  user={u}
                  key={u.id}
                  onAction={(action) => handleAction(u, action)}
                />
              ))}
          </Column>
        </Column>
      </Column>
    </InitiativeViewPageLayout>
  )
}

const mapRole = (tab: InitiativePeopleTab): InitiativeUserRole[] => {
  return match(tab)
    .with(INITIATIVE_PEOPLE_TABS.members, () => [INITIATIVE_USER_ROLES.member])
    .with(INITIATIVE_PEOPLE_TABS.followers, () => [
      INITIATIVE_USER_ROLES.follower,
    ])
    .with(INITIATIVE_PEOPLE_TABS.admins, () => [
      INITIATIVE_USER_ROLES.admin,
      INITIATIVE_USER_ROLES.owner,
    ])
    .with(INITIATIVE_PEOPLE_TABS.requests, () => [
      INITIATIVE_USER_ROLES.applicant,
    ])
    .exhaustive()
}
